import React, { useState, useEffect, forwardRef } from 'react';
import styles from '../../styles/AsignacionBecasStyles/AsignacionBecas.module.css';
import ScholarshipSummaryModal from './ScholarshipSummaryModal';
import { createStudentScholarship, deleteStudentScholarship, updateStudentScholarship } from '../../api/studentScholarships';

function StudentDetail(props, ref) {
  const { student, onBack, searchFilters = {}, searchTerm, onSearch } = props;
  const [activeTab, setActiveTab] = useState('asignar');
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [becasList, setBecasList] = useState([]);
  const [campusList, setCampusList] = useState([]);
  const [ciclosList, setCiclosList] = useState([]);
  const [usuariosList, setUsuariosList] = useState([]);
  const [becasActivas, setBecasActivas] = useState([]);
  const [guardando, setGuardando] = useState(false);
  
  // Estado para múltiples becas a asignar
  const [becasAAsignar, setBecasAAsignar] = useState([{
    id: Date.now(),
    tipoBeca: '',
    porcentajeBeca: '',
    montoBeca: '',
    fechaInicio: '',
    fechaFin: '',
    cicloEscolar: '',
    motivo: '',
    autorizadoPor: '',
    tipoBecaDetectado: null, // 'PORCENTAJE' o 'MONTO'
    porcentajeMaximo: null,
    montoMaximo: null,
    errorPorcentaje: null, // Mensaje de error para validación de porcentaje
    errorMonto: null // Mensaje de error para validación de monto
  }]);
  
  const [formDataRetiro, setFormDataRetiro] = useState({
    becaActiva: '',
    fechaRetiro: new Date().toLocaleDateString('es-MX'),
    motivoRetiro: '',
    descripcion: '',
    autorizadoPor: ''
  });

  // Estado para edición de becas
  const [editingBecaId, setEditingBecaId] = useState(null);
  const [editingBecaData, setEditingBecaData] = useState(null);
  const [editando, setEditando] = useState(false);

  useEffect(() => {
    loadDropdowns();
    loadBecasActivas();
  }, [student.id_estudiante]);

  const loadDropdowns = async () => {
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
    try {
      // Cargar becas activas
      const becasResponse = await fetch(`${API_BASE}/financiero/scholarships/?activo=true`);
      const becasData = await becasResponse.json();
      const becasArray = Array.isArray(becasData) ? becasData : (becasData.results || []);
      
      // Filtrar becas por campus del estudiante (si la beca tiene campus definido)
      const becasFiltradas = becasArray.filter(beca => {
        // Si la beca no tiene campus definido, está disponible para todos
        if (!beca.campus_id) return true;
        // Si el estudiante tiene campus, filtrar por ese campus
        if (student.id_campus) {
          return beca.campus_id === student.id_campus;
        }
        // Si el estudiante no tiene campus, mostrar todas
        return true;
      });
      
      console.log('[StudentDetail] Becas filtradas por campus:', becasFiltradas.length, 'de', becasArray.length);
      setBecasList(becasFiltradas);

      // Cargar campus
      const campusResponse = await fetch(`${API_BASE}/catalogos/campus/?activo=true`);
      const campusData = await campusResponse.json();
      const campusArray = Array.isArray(campusData) ? campusData : (campusData.results || []);
      setCampusList(campusArray);

      // Cargar ciclos escolares activos
      const ciclosResponse = await fetch(`${API_BASE}/catalogos/ciclos-escolares/?estado=ACTIVO`);
      const ciclosData = await ciclosResponse.json();
      console.log('[StudentDetail] Ciclos data from API:', ciclosData);
      const ciclosArray = Array.isArray(ciclosData) ? ciclosData : (ciclosData.results || []);
      console.log('[StudentDetail] Ciclos array:', ciclosArray);
      // Filtrar solo ciclos con id válido y añadir id_ciclo_escolar para compatibilidad
      const ciclosValidos = ciclosArray
        .filter(c => c.id != null && c.id !== undefined)
        .map(c => ({ ...c, id_ciclo_escolar: c.id }));
      console.log('[StudentDetail] Ciclos válidos:', ciclosValidos);
      setCiclosList(ciclosValidos);

      // Opciones locales para "Autorizado por"
      setUsuariosList([
        { id_usuario: 1, nombre_completo: 'Rector' },
        { id_usuario: 2, nombre_completo: 'Administracion' },
        { id_usuario: 3, nombre_completo: 'Director Academico' },
        { id_usuario: 4, nombre_completo: 'Sub Director' }
      ]);
    } catch (error) {
      console.error('Error al cargar dropdowns:', error);
    }
  };

  const loadBecasActivas = async () => {
    const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
    try {
      const response = await fetch(`${API_BASE}/financiero/estudiantes-becas/?id_estudiante=${student.id_estudiante}&estado_beca=ACTIVA`);
      const data = await response.json();
      const becasArray = Array.isArray(data) ? data : (data.results || []);
      console.log('[StudentDetail] Becas activas:', becasArray);
      setBecasActivas(becasArray);
      
      // Si hay becas activas, prellenar el formulario de retiro con la primera
      if (becasArray.length > 0) {
        const becaActiva = becasArray[0];
        setFormDataRetiro(prev => ({
          ...prev,
          becaActiva: becaActiva.id
        }));
      }
    } catch (error) {
      console.error('Error al cargar becas activas:', error);
    }
  };

  // Para input type="date" el formato es YYYY-MM-DD
  const formatDateToLocal = (dateStr) => {
    if (!dateStr) return '';
    // Si ya está en formato YYYY-MM-DD, devolverlo tal cual
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return dateStr;
    }
    // Si está en DD/MM/YYYY, convertir a YYYY-MM-DD
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  const formatDateToBackend = (dateStr) => {
    if (!dateStr) return '';
    // Si ya está en formato YYYY-MM-DD (del input date), devolverlo tal cual
    if (dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return dateStr;
    }
    // Si está en DD/MM/YYYY (legacy), convertir a YYYY-MM-DD
    const [day, month, year] = dateStr.split('/');
    return `${year}-${month}-${day}`;
  };

  // Agregar nueva beca al formulario
  const agregarBeca = () => {
    setBecasAAsignar(prev => [...prev, {
      id: Date.now(),
      tipoBeca: '',
      porcentajeBeca: '',
      montoBeca: '',
      fechaInicio: '',
      fechaFin: '',
      cicloEscolar: '',
      motivo: '',
      autorizadoPor: '',
      tipoBecaDetectado: null,
      porcentajeMaximo: null,
      montoMaximo: null,
      errorPorcentaje: null,
      errorMonto: null
    }]);
  };

  // Eliminar beca del formulario
  const eliminarBeca = (idBeca) => {
    if (becasAAsignar.length === 1) {
      alert('Debe haber al menos una beca en el formulario');
      return;
    }
    setBecasAAsignar(prev => prev.filter(b => b.id !== idBeca));
  };

  // Validar que el valor no exceda el máximo permitido de la beca
  const validarLimiteBeca = (valor, maximo, tipo) => {
    if (!valor || !maximo) return null;
    const valorNum = parseFloat(valor);
    const maximoNum = parseFloat(maximo);
    if (isNaN(valorNum) || isNaN(maximoNum)) return null;
    
    if (valorNum > maximoNum) {
      if (tipo === 'PORCENTAJE') {
        return `El porcentaje no puede exceder el máximo de ${maximoNum}% definido en la beca`;
      } else {
        return `El monto no puede exceder el máximo de $${maximoNum.toLocaleString('es-MX')} definido en la beca`;
      }
    }
    return null;
  };

  // Actualizar campo de una beca específica
  const actualizarBeca = (idBeca, campo, valor) => {
    setBecasAAsignar(prev => prev.map(beca => {
      if (beca.id !== idBeca) return beca;
      
      let nuevaBeca = { ...beca, [campo]: valor };
      
      // Si se cambia el tipo de beca, detectar tipo y actualizar máximos
      if (campo === 'tipoBeca') {
        const selectedBeca = becasList.find(b => b.id === parseInt(valor));
        if (selectedBeca) {
          const maxPorcentaje = selectedBeca.porcentaje_maximo ?? null;
          const maxMonto = selectedBeca.monto_maximo ?? null;
          
          // Determinar tipo de beca
          let tipo = null;
          if (maxPorcentaje !== null && maxPorcentaje > 0) {
            tipo = 'PORCENTAJE';
          } else if (maxMonto !== null && maxMonto > 0) {
            tipo = 'MONTO';
          } else {
            tipo = 'PORCENTAJE';
          }
          
          nuevaBeca = {
            ...nuevaBeca,
            tipoBecaDetectado: tipo,
            porcentajeMaximo: maxPorcentaje,
            montoMaximo: maxMonto,
            fechaInicio: selectedBeca.fecha_inicio ? formatDateToLocal(selectedBeca.fecha_inicio) : '',
            fechaFin: selectedBeca.fecha_fin ? formatDateToLocal(selectedBeca.fecha_fin) : '',
            // Limpiar errores al cambiar de beca
            errorPorcentaje: null,
            errorMonto: null,
            // Limpiar valores al cambiar de beca
            porcentajeBeca: '',
            montoBeca: ''
          };
        } else {
          nuevaBeca = {
            ...nuevaBeca,
            tipoBecaDetectado: null,
            porcentajeMaximo: null,
            montoMaximo: null,
            errorPorcentaje: null,
            errorMonto: null
          };
        }
      }
      
      // Validar porcentaje en tiempo real
      if (campo === 'porcentajeBeca') {
        const errorPorcentaje = validarLimiteBeca(valor, nuevaBeca.porcentajeMaximo, 'PORCENTAJE');
        nuevaBeca.errorPorcentaje = errorPorcentaje;
      }
      
      // Validar monto en tiempo real
      if (campo === 'montoBeca') {
        const errorMonto = validarLimiteBeca(valor, nuevaBeca.montoMaximo, 'MONTO');
        nuevaBeca.errorMonto = errorMonto;
      }
      
      return nuevaBeca;
    }));
  };

  const handleRetiroInputChange = (e) => {
    const { name, value } = e.target;
    setFormDataRetiro(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRetiroBeca = async (e) => {
    e.preventDefault();
    
    if (!formDataRetiro.becaActiva || !formDataRetiro.motivoRetiro || !formDataRetiro.autorizadoPor) {
      alert('Por favor complete todos los campos requeridos');
      return;
    }

    try {
      const { deleteStudentScholarship } = await import('../../api/studentScholarships');
      await deleteStudentScholarship(formDataRetiro.becaActiva);
      
      console.log('Beca retirada exitosamente');
      alert('Beca retirada exitosamente');
      
      // Recargar becas activas y regresar
      await loadBecasActivas();
      onBack();
    } catch (error) {
      console.error('Error al retirar beca:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
      alert('Error al retirar beca: ' + errorMsg);
    }
  };

  // Funciones para editar becas activas
  const abrirModalEdicion = (beca) => {
    setEditingBecaId(beca.id);
    setEditingBecaData({
      ...beca,
      fechaInicio: beca.fecha_inicio || '',
      fechaFin: beca.fecha_fin || ''
    });
  };

  const cerrarModalEdicion = () => {
    setEditingBecaId(null);
    setEditingBecaData(null);
  };

  const handleEditingInputChange = (e) => {
    const { name, value } = e.target;
    setEditingBecaData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGuardarEdicion = async () => {
    if (!editingBecaData) {
      return;
    }

    setEditando(true);
    try {
      // Con PATCH, enviamos las fechas + los valores actuales de porcentaje/monto
      // (necesarios para pasar la validación XOR del serializer)
      const updateData = {
        fecha_inicio: editingBecaData.fechaInicio ? formatDateToBackend(editingBecaData.fechaInicio) : null,
        fecha_fin: editingBecaData.fechaFin ? formatDateToBackend(editingBecaData.fechaFin) : null
      };

      // Incluir porcentaje o monto según el tipo de beca actual
      if (editingBecaData.porcentaje_otorgado && editingBecaData.porcentaje_otorgado > 0) {
        updateData.porcentaje_otorgado = editingBecaData.porcentaje_otorgado;
      } else if (editingBecaData.monto_beca_asignado && editingBecaData.monto_beca_asignado > 0) {
        updateData.monto_beca_asignado = editingBecaData.monto_beca_asignado;
      }

      console.log('[Edición Beca] Actualizando beca ID:', editingBecaId);
      console.log('[Edición Beca] Datos a enviar (PATCH):', updateData);
      
      await updateStudentScholarship(editingBecaId, updateData);
      
      alert('Beca actualizada exitosamente');
      await loadBecasActivas();
      cerrarModalEdicion();
    } catch (error) {
      console.error('Error al actualizar beca:', error);
      const errorMsg = error.response?.data ? JSON.stringify(error.response.data) : error.message;
      alert('Error al actualizar beca: ' + errorMsg);
    } finally {
      setEditando(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar que hay al menos una beca con datos
    const becasValidas = becasAAsignar.filter(b => b.tipoBeca);
    
    if (becasValidas.length === 0) {
      alert('Por favor seleccione al menos una beca');
      return;
    }
    
    // Evitar seleccionar la misma beca dos veces en el mismo formulario
    const idsSeleccionados = becasValidas.map(b => parseInt(b.tipoBeca));
    const idsDuplicados = idsSeleccionados.filter((id, idx) => idsSeleccionados.indexOf(id) !== idx);
    if (idsDuplicados.length > 0) {
      const becaInfo = becasList.find(b => b.id === idsDuplicados[0]);
      alert(`La beca "${becaInfo?.nombre || 'seleccionada'}" está duplicada en el formulario. Elimine una de las filas repetidas antes de continuar.`);
      return;
    }
    
    // Validar cada beca
    for (const beca of becasValidas) {
      if (beca.tipoBecaDetectado === 'PORCENTAJE' && !beca.porcentajeBeca) {
        const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
        alert(`Por favor ingrese el porcentaje para la beca "${becaInfo?.nombre || 'seleccionada'}"`);
        return;
      }
      
      if (beca.tipoBecaDetectado === 'MONTO' && !beca.montoBeca) {
        const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
        alert(`Por favor ingrese el monto para la beca "${becaInfo?.nombre || 'seleccionada'}"`);
        return;
      }
      
      // Validar que no se exceda el límite máximo de la beca
      if (beca.errorPorcentaje || beca.errorMonto) {
        const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
        const errorMsg = beca.errorPorcentaje || beca.errorMonto;
        alert(`Error en beca "${becaInfo?.nombre || 'seleccionada'}": ${errorMsg}`);
        return;
      }
      
      // Validación adicional: verificar que el porcentaje no exceda el máximo
      if (beca.tipoBecaDetectado === 'PORCENTAJE' && beca.porcentajeMaximo) {
        const porcentajeIngresado = parseFloat(beca.porcentajeBeca);
        const porcentajeMax = parseFloat(beca.porcentajeMaximo);
        if (porcentajeIngresado > porcentajeMax) {
          const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
          alert(`El porcentaje ingresado (${porcentajeIngresado}%) excede el máximo permitido (${porcentajeMax}%) para la beca "${becaInfo?.nombre || 'seleccionada'}"`);
          return;
        }
      }
      
      // Validación adicional: verificar que el monto no exceda el máximo
      if (beca.tipoBecaDetectado === 'MONTO' && beca.montoMaximo) {
        const montoIngresado = parseFloat(beca.montoBeca);
        const montoMax = parseFloat(beca.montoMaximo);
        if (montoIngresado > montoMax) {
          const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
          alert(`El monto ingresado ($${montoIngresado.toLocaleString('es-MX')}) excede el máximo permitido ($${montoMax.toLocaleString('es-MX')}) para la beca "${becaInfo?.nombre || 'seleccionada'}"`);
          return;
        }
      }
    }

    setGuardando(true);
    let becasGuardadas = 0;
    let errores = [];

    try {
      for (const beca of becasValidas) {
        // Obtener el ciclo escolar
        const cicloEscolar = beca.cicloEscolar || 
                            student.id_ciclo_escolar || 
                            (ciclosList.length > 0 ? ciclosList[0].id_ciclo_escolar : null);
        
        if (!cicloEscolar) {
          errores.push('No se pudo determinar el ciclo escolar');
          continue;
        }

        // Obtener el nombre del usuario seleccionado
        const autorizadoPorNombre = beca.autorizadoPor ? 
          usuariosList.find(u => u.id_usuario === parseInt(beca.autorizadoPor))?.nombre_completo : null;

        // Construir payload según tipo de beca
        const payload = {
          id_estudiante: student.id_estudiante,
          id_beca: parseInt(beca.tipoBeca),
          id_ciclo_escolar: parseInt(cicloEscolar),
          motivo_otorgamiento: beca.motivo || 'Sin motivo especificado', // Motivo opcional
          fecha_inicio: beca.fechaInicio ? formatDateToBackend(beca.fechaInicio) : null,
          fecha_fin: beca.fechaFin ? formatDateToBackend(beca.fechaFin) : null,
          estado_beca: 'ACTIVA',
          autorizado_por_nombre: autorizadoPorNombre
        };

        // Agregar campo según tipo de beca
        if (beca.tipoBecaDetectado === 'PORCENTAJE') {
          payload.porcentaje_otorgado = parseFloat(beca.porcentajeBeca);
          if (beca.montoMaximo && beca.porcentajeBeca) {
            const montoBecaCalc = parseFloat(beca.montoMaximo) * (parseFloat(beca.porcentajeBeca) / 100);
            payload.monto_beca_calculado_input = parseFloat(montoBecaCalc.toFixed(2));
          }
        } else if (beca.tipoBecaDetectado === 'MONTO') {
          payload.monto_beca_asignado = parseFloat(beca.montoBeca);
        }

        console.log('=== ENVIANDO BECA ===');
        console.log('Beca:', beca.tipoBeca, 'Tipo:', beca.tipoBecaDetectado);
        console.log('Payload:', JSON.stringify(payload, null, 2));
        
        try {
          await createStudentScholarship(payload);
          becasGuardadas++;
        } catch (err) {
          const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
          const errorMsg = err.response?.data ? JSON.stringify(err.response.data) : err.message;
          errores.push(`${becaInfo?.nombre || 'Beca'}: ${errorMsg}`);
        }
      }
      
      if (becasGuardadas > 0) {
        alert(`${becasGuardadas} beca(s) asignada(s) exitosamente${errores.length > 0 ? `\n\nErrores:\n${errores.join('\n')}` : ''}`);
        
        // Limpiar formulario
        setBecasAAsignar([{
          id: Date.now(),
          tipoBeca: '',
          porcentajeBeca: '',
          montoBeca: '',
          fechaInicio: '',
          fechaFin: '',
          cicloEscolar: '',
          motivo: '',
          autorizadoPor: '',
          tipoBecaDetectado: null,
          porcentajeMaximo: null,
          montoMaximo: null,
          errorPorcentaje: null,
          errorMonto: null
        }]);
        
        // Recargar becas activas
        await loadBecasActivas();
      } else {
        alert('No se pudo guardar ninguna beca:\n' + errores.join('\n'));
      }
      
    } catch (error) {
      console.error('Error al asignar becas:', error);
      alert('Error al asignar becas: ' + error.message);
    } finally {
      setGuardando(false);
    }
  };

  const handleHistorial = () => {
    console.log('Ver historial de becas para:', student.nombre_completo);
  };

  const handleCloseSummaryModal = () => {
    setShowSummaryModal(false);
  };

  const handleConfirmScholarship = () => {
    console.log('Beca confirmada para:', student.nombre_completo);
    setShowSummaryModal(false);
  };

  return (
    <div className={styles.studentDetailContainer}>

      {/* Student Info Card */}
      <div className={styles.studentCard}>
        <div className={styles.studentCardHeader}>
          <span className={styles.studentCardTitle}>Estudiante: {student.nombre_completo}</span>
        </div>
        <div className={styles.studentCardContent}>
          <div className={styles.studentAvatar}>
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <div className={styles.studentInfo}>
            <div className={styles.studentInfoGrid}>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Matrícula</span>
                <span className={styles.studentInfoValue}>{student.matricula}</span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Período actual</span>
                <span className={styles.studentInfoValue}>{student.ciclo_escolar_nombre || 'N/A'}</span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Becas Activas</span>
                <span className={styles.studentInfoValue}>
                  {becasActivas.length > 0 
                    ? becasActivas.map(b => b.beca_nombre).join(', ')
                    : 'Sin becas'}
                </span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Sección</span>
                <span className={styles.studentInfoValue}>{student.seccion_nombre || 'N/A'}</span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Institución</span>
                <span className={styles.studentInfoValue}>{student.institucion_nombre || 'N/A'}</span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Programa</span>
                <span className={styles.studentInfoValue}>{student.programa_nombre || 'N/A'}</span>
              </div>
              <div className={styles.studentInfoItem}>
                <span className={styles.studentInfoLabel}>Campus</span>
                <span className={styles.studentInfoValue}>{student.campus_nombre || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scholarship Assignment Form */}
      <div className={styles.scholarshipForm}>
        <div className={styles.formHeader}>
          <div className={styles.formTabs}>
            <button 
              className={`${styles.formTab} ${activeTab === 'asignar' ? styles.formTabActive : ''}`}
              onClick={() => setActiveTab('asignar')}
            >
              Asignar Beca
            </button>
            <button 
              className={`${styles.formTab} ${activeTab === 'retirar' ? styles.formTabActive : ''}`}
              onClick={() => setActiveTab('retirar')}
            >
              Retirar Beca
            </button>
          </div>
          <button className={styles.historialButton} onClick={handleHistorial}>
            <svg className={styles.historialIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Historial
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.formContent}>
          {activeTab === 'asignar' ? (
            <div>
              {/* Mostrar becas activas del estudiante */}
              {becasActivas.length > 0 && (
                <div style={{
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #bbf7d0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '20px'
                }}>
                  <div style={{ fontWeight: '600', color: '#166534', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '16px' }}>🎓</span>
                    Becas Activas del Estudiante ({becasActivas.length})
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {becasActivas.map((beca) => {
                      const esPorcentaje = beca.porcentaje_otorgado !== null && beca.porcentaje_otorgado > 0;
                      return (
                        <div key={`activa-chip-${beca.id}`} style={{
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '13px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{
                            backgroundColor: esPorcentaje ? '#dbeafe' : '#fef3c7',
                            color: esPorcentaje ? '#1e40af' : '#92400e',
                            padding: '1px 5px',
                            borderRadius: '3px',
                            fontSize: '10px',
                            fontWeight: '700'
                          }}>
                            {esPorcentaje ? '%' : '$'}
                          </span>
                          <span style={{ fontWeight: '500' }}>{beca.beca_nombre}</span>
                          <span style={{ color: '#6b7280' }}>
                            {esPorcentaje 
                              ? `${beca.porcentaje_otorgado}%`
                              : `$${parseFloat(beca.monto_beca_asignado || beca.monto_beca_calculado || 0).toLocaleString('es-MX')}`
                            }
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Formulario de múltiples becas */}
              {becasAAsignar.map((beca, index) => {
                const becaInfo = becasList.find(b => b.id === parseInt(beca.tipoBeca));
                return (
                  <div key={beca.id} style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '16px',
                    backgroundColor: index % 2 === 0 ? '#fafafa' : '#ffffff'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: '600', color: '#374151' }}>
                        Beca #{index + 1}
                        {becaInfo && (
                          <span style={{
                            marginLeft: '10px',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: '600',
                            backgroundColor: beca.tipoBecaDetectado === 'PORCENTAJE' ? '#dbeafe' : beca.tipoBecaDetectado === 'MONTO' ? '#fef3c7' : '#f3f4f6',
                            color: beca.tipoBecaDetectado === 'PORCENTAJE' ? '#1e40af' : beca.tipoBecaDetectado === 'MONTO' ? '#92400e' : '#6b7280'
                          }}>
                            {beca.tipoBecaDetectado === 'PORCENTAJE' ? '% Porcentaje' : beca.tipoBecaDetectado === 'MONTO' ? '$ Monto Fijo' : 'Sin seleccionar'}
                          </span>
                        )}
                      </span>
                      {becasAAsignar.length > 1 && (
                        <button
                          type="button"
                          onClick={() => eliminarBeca(beca.id)}
                          style={{
                            background: '#fee2e2',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '4px 10px',
                            cursor: 'pointer',
                            color: '#dc2626',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}
                        >
                          ✕ Eliminar
                        </button>
                      )}
                    </div>
                    
                    <div className={styles.formGrid}>
                      {/* Tipo de beca */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Tipo de beca <span style={{color: 'red'}}>*</span></label>
                        <select
                          value={beca.tipoBeca}
                          onChange={(e) => actualizarBeca(beca.id, 'tipoBeca', e.target.value)}
                          className={styles.formSelect}
                        >
                          <option value="">— Seleccione tipo de beca —</option>
                          {becasList.map((b) => (
                            <option key={`beca-${beca.id}-${b.id}`} value={b.id}>
                              {b.nombre} {b.porcentaje_maximo ? `(% max: ${b.porcentaje_maximo})` : b.monto_maximo ? `($${parseFloat(b.monto_maximo).toLocaleString('es-MX')})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Campos de porcentaje y monto (siempre visibles, uno editable) */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Porcentaje de beca {beca.tipoBecaDetectado === 'PORCENTAJE' && <span style={{color: 'red'}}>*</span>}
                        </label>
                        <input
                          type="number"
                          value={beca.porcentajeBeca}
                          onChange={(e) => actualizarBeca(beca.id, 'porcentajeBeca', e.target.value)}
                          className={styles.formInput}
                          placeholder="0"
                          min="0"
                          max={beca.porcentajeMaximo || 100}
                          step="0.01"
                          disabled={beca.tipoBecaDetectado === 'MONTO'}
                          style={{ 
                            backgroundColor: beca.tipoBecaDetectado === 'MONTO' ? '#f3f4f6' : 'white',
                            borderColor: beca.errorPorcentaje ? '#dc2626' : undefined,
                            boxShadow: beca.errorPorcentaje ? '0 0 0 1px #dc2626' : undefined
                          }}
                        />
                        {beca.porcentajeMaximo && beca.tipoBecaDetectado === 'PORCENTAJE' && !beca.errorPorcentaje && (
                          <small style={{ color: '#6b7280', fontSize: '11px' }}>
                            Máximo: {beca.porcentajeMaximo}%
                          </small>
                        )}
                        {beca.errorPorcentaje && (
                          <small style={{ color: '#dc2626', fontSize: '11px', fontWeight: '500' }}>
                            {beca.errorPorcentaje}
                          </small>
                        )}
                      </div>

                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>
                          Monto de beca {beca.tipoBecaDetectado === 'MONTO' && <span style={{color: 'red'}}>*</span>}
                        </label>
                        <input
                          type="number"
                          value={beca.montoBeca}
                          onChange={(e) => actualizarBeca(beca.id, 'montoBeca', e.target.value)}
                          className={styles.formInput}
                          placeholder="0.00"
                          min="0"
                          max={beca.montoMaximo || undefined}
                          step="0.01"
                          disabled={beca.tipoBecaDetectado === 'PORCENTAJE'}
                          style={{ 
                            backgroundColor: beca.tipoBecaDetectado === 'PORCENTAJE' ? '#f3f4f6' : 'white',
                            borderColor: beca.errorMonto ? '#dc2626' : undefined,
                            boxShadow: beca.errorMonto ? '0 0 0 1px #dc2626' : undefined
                          }}
                        />
                        {beca.montoMaximo && beca.tipoBecaDetectado === 'MONTO' && !beca.errorMonto && (
                          <small style={{ color: '#6b7280', fontSize: '11px' }}>
                            Máximo: ${parseFloat(beca.montoMaximo).toLocaleString('es-MX')}
                          </small>
                        )}
                        {beca.errorMonto && (
                          <small style={{ color: '#dc2626', fontSize: '11px', fontWeight: '500' }}>
                            {beca.errorMonto}
                          </small>
                        )}
                      </div>

                      {/* Ciclo escolar */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Ciclo Escolar</label>
                        <select
                          value={beca.cicloEscolar}
                          onChange={(e) => actualizarBeca(beca.id, 'cicloEscolar', e.target.value)}
                          className={styles.formSelect}
                        >
                          <option value="">— Seleccione ciclo escolar —</option>
                          {ciclosList.map((ciclo) => (
                            <option key={`ciclo-${beca.id}-${ciclo.id}`} value={ciclo.id}>
                              {ciclo.nombre}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Fecha inicio */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Fecha de Inicio</label>
                        <input
                          type="date"
                          value={beca.fechaInicio}
                          onChange={(e) => actualizarBeca(beca.id, 'fechaInicio', e.target.value)}
                          className={styles.formInput}
                        />
                      </div>

                      {/* Fecha fin */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Fecha de Fin</label>
                        <input
                          type="date"
                          value={beca.fechaFin}
                          onChange={(e) => actualizarBeca(beca.id, 'fechaFin', e.target.value)}
                          className={styles.formInput}
                        />
                      </div>

                      {/* Motivo (opcional) */}
                      <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                        <label className={styles.formLabel}>Motivo</label>
                        <textarea
                          value={beca.motivo}
                          onChange={(e) => actualizarBeca(beca.id, 'motivo', e.target.value)}
                          className={styles.formTextarea}
                          rows={2}
                          placeholder="Opcional - Ingrese el motivo de la beca"
                        />
                      </div>

                      {/* Autorizado por */}
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Autorizado por</label>
                        <select
                          value={beca.autorizadoPor}
                          onChange={(e) => actualizarBeca(beca.id, 'autorizadoPor', e.target.value)}
                          className={styles.formSelect}
                        >
                          <option value="">— Seleccione —</option>
                          {usuariosList.map((usuario) => (
                            <option key={`usuario-${beca.id}-${usuario.id_usuario}`} value={usuario.id_usuario}>
                              {usuario.nombre_completo}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Botón agregar otra beca */}
              <div style={{ marginBottom: '20px' }}>
                <button
                  type="button"
                  onClick={agregarBeca}
                  style={{
                    background: '#f0fdf4',
                    border: '2px dashed #22c55e',
                    borderRadius: '8px',
                    padding: '12px 20px',
                    cursor: 'pointer',
                    color: '#166534',
                    fontSize: '14px',
                    fontWeight: '500',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px'
                  }}
                >
                  <span style={{ fontSize: '18px' }}>+</span>
                  Agregar otra beca
                </button>
              </div>

              <div className={styles.formActions}>
                <button 
                  type="submit" 
                  className={styles.applyButton} 
                  disabled={guardando || becasAAsignar.some(b => b.errorPorcentaje || b.errorMonto)}
                  style={becasAAsignar.some(b => b.errorPorcentaje || b.errorMonto) ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                >
                  {guardando ? 'GUARDANDO...' : `APLICAR ${becasAAsignar.filter(b => b.tipoBeca).length} BECA(S)`}
                </button>
                {becasAAsignar.some(b => b.errorPorcentaje || b.errorMonto) && (
                  <small style={{ color: '#dc2626', fontSize: '12px', marginTop: '8px', display: 'block', textAlign: 'center' }}>
                    Corrija los errores de validación antes de guardar
                  </small>
                )}
              </div>
            </div>
          ) : (
            <div className={styles.retirarBecaContent}>
              <div className={styles.becasActivasSection}>
                <div className={styles.sectionHeader}>
                  <div className={styles.sectionTitleContainer}>
                    <div className={styles.sectionIcon}>
                      <svg viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </div>
                    <span className={styles.sectionTitle}>Becas Activas</span>
                  </div>
                </div>
                
                <div className={styles.becasActivasTable}>
                  <table className={styles.activeBecasTable}>
                    <thead>
                      <tr>
                        <th>Tipo de Beca</th>
                        <th>Tipo</th>
                        <th>Valor</th>
                        <th>Fecha inicio</th>
                        <th>Fecha fin</th>
                        <th>Autorizado por</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {becasActivas.length > 0 ? (
                        becasActivas.map((beca) => {
                          // Determinar tipo basado en valores
                          const esPorcentaje = beca.porcentaje_otorgado !== null && beca.porcentaje_otorgado > 0;
                          const esMonto = beca.monto_beca_asignado !== null && beca.monto_beca_asignado > 0;
                          const tipoBeca = esPorcentaje ? 'PORCENTAJE' : esMonto ? 'MONTO' : (beca.tipo_beca || 'PORCENTAJE');
                          
                          return (
                            <tr key={`beca-activa-${beca.id}`}>
                              <td>{beca.beca_nombre}</td>
                              <td>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '600',
                                  backgroundColor: tipoBeca === 'PORCENTAJE' ? '#dbeafe' : '#fef3c7',
                                  color: tipoBeca === 'PORCENTAJE' ? '#1e40af' : '#92400e'
                                }}>
                                  {tipoBeca === 'PORCENTAJE' ? '%' : '$'}
                                </span>
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                {tipoBeca === 'PORCENTAJE' 
                                  ? `${beca.porcentaje_otorgado}%`
                                  : `$${parseFloat(beca.monto_beca_asignado || beca.monto_beca_calculado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`
                                }
                              </td>
                              <td>{formatDateToLocal(beca.fecha_inicio)}</td>
                              <td>{beca.fecha_fin ? formatDateToLocal(beca.fecha_fin) : 'N/A'}</td>
                              <td>{beca.autorizado_nombre || 'N/A'}</td>
                              <td>
                                <button
                                  type="button"
                                  onClick={() => abrirModalEdicion(beca)}
                                  style={{
                                    padding: '4px 12px',
                                    backgroundColor: '#3b82f6',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    transition: 'background-color 0.2s'
                                  }}
                                  onMouseOver={(e) => e.target.style.backgroundColor = '#2563eb'}
                                  onMouseOut={(e) => e.target.style.backgroundColor = '#3b82f6'}
                                >
                                  ✎ Editar
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="7" style={{textAlign: 'center'}}>No hay becas activas</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Modal de Edición de Beca */}
              {editingBecaId && editingBecaData && (
                <div style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1000
                }}>
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '30px',
                    maxWidth: '500px',
                    width: '90%',
                    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)'
                  }}>
                    <h2 style={{ marginTop: 0, marginBottom: '20px', color: '#1a1a2e' }}>
                      Editar Fechas de Beca: {editingBecaData.beca_nombre}
                    </h2>
                    
                    <div className={styles.formGroup} style={{ marginBottom: '16px' }}>
                      <label className={styles.formLabel}>Fecha de Inicio</label>
                      <input
                        type="date"
                        name="fechaInicio"
                        value={editingBecaData.fechaInicio}
                        onChange={handleEditingInputChange}
                        className={styles.formInput}
                      />
                    </div>

                    <div className={styles.formGroup} style={{ marginBottom: '24px' }}>
                      <label className={styles.formLabel}>Fecha de Fin</label>
                      <input
                        type="date"
                        name="fechaFin"
                        value={editingBecaData.fechaFin}
                        onChange={handleEditingInputChange}
                        className={styles.formInput}
                      />
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        type="button"
                        onClick={cerrarModalEdicion}
                        disabled={editando}
                        style={{
                          padding: '10px 24px',
                          backgroundColor: '#e5e7eb',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          color: '#374151'
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={handleGuardarEdicion}
                        disabled={editando}
                        style={{
                          padding: '10px 24px',
                          backgroundColor: editando ? '#9ca3af' : '#3b82f6',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: editando ? 'not-allowed' : 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        {editando ? 'Guardando...' : 'Guardar Cambios'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className={styles.retirarFormGrid}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Beca Activa</label>
                  <select 
                    name="becaActiva"
                    value={formDataRetiro.becaActiva}
                    onChange={handleRetiroInputChange}
                    className={styles.formSelect}
                  >
                    <option value="">— Seleccionar beca —</option>
                    {becasActivas.map((beca) => (
                      <option key={`retiro-beca-${beca.id}`} value={beca.id}>
                        {beca.beca_nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Fecha de retiro</label>
                  <input
                    type="text"
                    name="fechaRetiro"
                    value={formDataRetiro.fechaRetiro}
                    className={styles.formInput}
                    readOnly
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Motivo</label>
                  <select 
                    name="motivoRetiro"
                    value={formDataRetiro.motivoRetiro}
                    onChange={handleRetiroInputChange}
                    className={styles.formSelect}
                  >
                    <option value="">— Seleccionar motivo —</option>
                    <option value="academico">Motivo Académico</option>
                    <option value="economico">Motivo Económico</option>
                    <option value="disciplinario">Motivo Disciplinario</option>
                  </select>
                </div>

                <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                  <label className={styles.formLabel}>Descripción</label>
                  <textarea
                    name="descripcion"
                    value={formDataRetiro.descripcion}
                    onChange={handleRetiroInputChange}
                    className={styles.formTextarea}
                    rows={3}
                    placeholder="Descripción del motivo del retiro..."
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>Autorizado por</label>
                  <select 
                    name="autorizadoPor"
                    value={formDataRetiro.autorizadoPor}
                    onChange={handleRetiroInputChange}
                    className={styles.formSelect}
                  >
                    <option value="">— Seleccione —</option>
                    {usuariosList.map((usuario) => (
                      <option key={`retiro-usuario-${usuario.id_usuario}`} value={usuario.id_usuario}>
                        {usuario.nombre_completo}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.formActions}>
                <button type="button" onClick={handleRetiroBeca} className={styles.applyButton}>
                  APLICAR
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Scholarship Summary Modal */}
      <ScholarshipSummaryModal
        isOpen={showSummaryModal}
        onClose={handleCloseSummaryModal}
        onConfirm={handleConfirmScholarship}
        student={student}
      />
    </div>
  );
}

export default forwardRef(StudentDetail);
